import { Fragment, useEffect, useRef, useState } from "react";
import moment from "moment";
import { Line, getElementAtEvent } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useAnalyticsWrapper } from "../../../../../store/store";
import { Child, Container, Header, LayerContent } from "../style";
import { NoData } from "../../style";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import { BAR_PALETTE, lineOptions } from "../formUtils";
import SectionModal from "./SectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { woundAnalysisDetailCols } from "../../dummyData/colums";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

// Section 10 — Skin Injury Type by Month.
// Reads woundAnalysisIncidents.skinInjuryTypeByMonth off the consolidated /analytic/wound-analysis payload.
// Shape: { months: string[], injuryByMonth: [{ injuryType, monthlyCounts: {<label>: n}, total, percentage }],
//          drillDownByType: { <injuryType>: { <monthLabel>: incident[] } } }
const SkinInjuryTypeByMonth = ({ setClickedIncident }: any) => {
  const [months, setMonths] = useState<string[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [chartData, setChartData] = useState<any>(null);
  const [selected, setSelected] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [details, setDetails] = useState<any>([]);
  const lineRef = useRef<any>(null);

  const { startDate, endDate, woundFacility, woundAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      woundFacility: state.woundFacility,
      woundAnalysisIncidents: state.woundAnalysisIncidents,
    }));

  useEffect(() => {
    const section = woundAnalysisIncidents?.skinInjuryTypeByMonth;
    const injuryByMonth = section?.injuryByMonth;
    const monthLabels: string[] = section?.months ?? [];

    if (injuryByMonth?.length) {
      // Flatten each row's monthlyCounts map into top-level fields so Kendo can bind them.
      const rows = injuryByMonth.map((row: any) => {
        const flat: Record<string, any> = {
          injuryType: row.injuryType,
          total: row.total,
          percentage: row.percentage,
        };
        monthLabels.forEach((m) => {
          flat[m] = row.monthlyCounts?.[m] ?? 0;
        });
        return flat;
      });

      // Total row: sum each month column + the total column. Each incident maps to exactly one
      // injury type, so the per-type percentages sum to 100%.
      const totalRow: Record<string, any> = {
        injuryType: "Total",
        total: rows.reduce((s: number, r: any) => s + (r.total ?? 0), 0),
        percentage: 100,
      };
      monthLabels.forEach((m) => {
        totalRow[m] = rows.reduce((s: number, r: any) => s + (r[m] ?? 0), 0);
      });
      rows.push(totalRow);

      setMonths(monthLabels);
      setTableData(rows);

      // Chart month window follows the §2 Monthly Trend chart (first→last month that actually has
      // data) instead of the full report param range — the param range leaves the line chart too
      // crowded with empty months. The table above still shows every month in the report range.
      // §2's monthlyTrend[].month is "YYYY-MM"; our monthlyCounts keys are "MMM yy", so we rebuild
      // the contiguous window in that same label format and index straight into monthlyCounts.
      const trend = woundAnalysisIncidents?.skinIncidentTrend?.monthlyTrend ?? [];
      let chartMonths = monthLabels;
      if (trend.length) {
        const start = moment(trend[0].month);
        const end = moment(trend[trend.length - 1].month);
        const labels: string[] = [];
        const cur = start.clone();
        while (cur.isSameOrBefore(end, "month")) {
          labels.push(cur.format("MMM YY"));
          cur.add(1, "month");
        }
        chartMonths = labels;
      }

      // One line per injury type across the §2 window (excludes the Total row).
      // Point/line config is field-for-field identical to the §2 Monthly Trend datasets so the
      // two charts render the same way; only the per-type color differs (BAR_PALETTE).
      setChartData({
        labels: chartMonths,
        datasets: injuryByMonth.map((row: any, i: number) => {
          const color = BAR_PALETTE[i % BAR_PALETTE.length];
          return {
            label: row.injuryType,
            data: chartMonths.map((m) => row.monthlyCounts?.[m] ?? 0),
            fill: false,
            borderColor: color,
            pointBorderWidth: 5,
            pointRadius: 10,
            pointHoverRadius: 15,
            tension: 0.4,
            pointHoverBorderWidth: 5,
            yAxisID: "y",
          };
        }),
      });
    } else {
      setMonths([]);
      setTableData([]);
      setChartData(null);
    }
  }, [woundAnalysisIncidents]);

  const title = "Skin Injury Type by Month";

  // Click a bubble → resolve (injury type from the dataset, month from the x index) to its incidents.
  const handleChartClicked = (element: any) => {
    if (!element) return;
    const monthLabel = chartData?.labels?.[element.index];
    const injuryType = chartData?.datasets?.[element.datasetIndex]?.label;
    const byType =
      woundAnalysisIncidents?.skinInjuryTypeByMonth?.drillDownByType?.[
        injuryType
      ];
    setSelected(`${injuryType} — ${monthLabel}`);
    setDetails(byType?.[monthLabel] || []);
    setShowModal(true);
  };

  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };
    // Fixed column widths so the grid overflows its container and Kendo shows a
    // horizontal scrollbar once there are enough month columns.
    const newGrid = (
      <Fragment>
        <Grid data={tableData || []} scrollable="scrollable" rows={{ data: TotalRow }}>
          <GridColumn field="injuryType" title="Skin Injury Type" width="150px" />
          {months.map((m) => (
            <GridColumn
              key={m}
              field={m}
              title={m}
              width="64px"
              {...(cellCountProps as GridColumnProps)}
            />
          ))}
          <GridColumn
            field="total"
            title="Total"
            width="70px"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            width="60px"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(newGrid);
  }, [tableData, months]);

  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="58%">
          {tableData.length > 0 ? (
            <div style={{ overflowX: "auto", width: "100%" }}>{grid}</div>
          ) : (
            <NoData>No Data</NoData>
          )}
        </Child>
        <Child flex="42%">
          {chartData ? (
            <Line
              ref={lineRef}
              data={chartData}
              options={{ ...lineOptions, aspectRatio: 1.3 }}
              onClick={(event) => {
                // Guard the ref (early clicks during mount can fire before it's set) and ignore
                // clicks that don't land on a point.
                if (!lineRef.current) return;
                const clickedElement = getElementAtEvent(lineRef.current, event)[0];
                if (!clickedElement) return;
                handleChartClicked(clickedElement);
              }}
            />
          ) : null}
        </Child>
      </Container>
      {showModal ? (
        <SectionModal
          open={showModal}
          facilityName={(woundFacility?.label ?? woundFacility?.[0]?.label)}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {}}
          isLoading={false}
          tableData={details}
          tableCols={woundAnalysisDetailCols()}
          title={`${title} - ${selected}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: details,
              columnData: woundAnalysisDetailCols(),
              fileName: (woundFacility?.label ?? woundFacility?.[0]?.label),
              startDate,
              endDate,
              subTitle: `${title} - ${selected}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              details,
              woundAnalysisDetailCols(),
              `${(woundFacility?.label ?? woundFacility?.[0]?.label)} - ${title} - ${selected}`
            )
          }
        />
      ) : null}
    </LayerContent>
  );
};

export default SkinInjuryTypeByMonth;
